import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  EmptyState,
  Flex,
  Heading,
  Icon,
  Icons,
  Modal,
  Pagination,
  Paragraph,
  Spin,
  Table,
  type TableColumnType,
} from "@ir/ui";
import { AppConfig } from "@src/config/AppConfig";
import { GetMasterDataDistributionCentersDTO } from "@src/domain/dto/masterData";
import type { MasterDataDistributionCenter as MasterDataDistributionCenterEntity } from "@src/domain/entities/masterData";
import { Permissions } from "@src/domain/permissions";
import { GetMasterDataDistributionCentersUseCase } from "@src/application/useCases/masterData";
import { MasterDataDistributionCenterRepository } from "@src/infrastructure/repositories/masterData";
import HasAnyPermission from "@src/presentation/components/permissions/HasAnyPermission";
import { useMasterDataPurchasers } from "@src/presentation/providers/MasterDataPurchasersDataProvider";
import {
  getMasterDataListCacheEntry,
  invalidateMasterDataListCacheScopes,
  masterDataPurchaserDcCacheKey,
  setMasterDataListCacheEntry,
} from "@src/presentation/providers/masterData/masterDataListCache";
import { MasterDataDistributionCenterAddEditForm } from "./MasterDataDistributionCenterAddEditForm";
import {
  MASTER_DATA_COLUMN_VERTICAL_ALIGN,
  renderMasterDataTextCell,
  sumMasterDataColumnWidths,
} from "./masterDataTable.shared";
import {
  buildDistributionCenterActionsColumn,
  indexDistributionCentersById,
  mapDistributionCentersToRows,
  type MasterDataDistributionCenterRow,
} from "./masterDataDistributionCenterTable.shared";
import { useMasterDataDistributionCenterForm } from "./useMasterDataDistributionCenterForm";

interface MasterDataPurchaserDcModalProps {
  isOpen: boolean;
  purchaserId: number | null;
  purchaserName: string;
  onClose: () => void;
}

function buildColumns(
  onEdit: (entity: MasterDataDistributionCenterEntity) => void,
  centersById: Map<number, MasterDataDistributionCenterEntity>,
): TableColumnType<MasterDataDistributionCenterRow>[] {
  return [
    {
      title: "DC Name",
      dataIndex: "dcName",
      key: "dcName",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 220,
    },
    {
      title: "DC Code",
      dataIndex: "dcCode",
      key: "dcCode",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 140,
    },
    {
      title: "Purchaser Name",
      dataIndex: "purchaserName",
      key: "purchaserName",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 200,
      cellRender: renderMasterDataTextCell,
    },
    buildDistributionCenterActionsColumn(onEdit, centersById),
  ];
}

/** Modal listing Distribution Centers for one purchaser (DC matrix eye). */
export const MasterDataPurchaserDcModal = ({
  isOpen,
  purchaserId,
  purchaserName,
  onClose,
}: MasterDataPurchaserDcModalProps) => {
  const { brandKey, refetch: refetchPurchasers } = useMasterDataPurchasers();
  const pageSize = AppConfig.getDefaultPaginationLimit();
  const [page, setPage] = useState(1);
  const [activePurchaserId, setActivePurchaserId] = useState(purchaserId);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [distributionCenters, setDistributionCenters] = useState<
    MasterDataDistributionCenterEntity[]
  >([]);

  if (purchaserId !== activePurchaserId) {
    setActivePurchaserId(purchaserId);
    setPage(1);
    setDistributionCenters([]);
    setTotal(0);
    setError(null);
  }

  const cacheKey =
    isOpen && purchaserId != null
      ? masterDataPurchaserDcCacheKey(brandKey, purchaserId, page, pageSize)
      : null;
  const cached = cacheKey
    ? getMasterDataListCacheEntry<MasterDataDistributionCenterEntity>(cacheKey)
    : undefined;
  const items = cached?.items ?? distributionCenters;
  const itemTotal = cached?.total ?? total;
  const loading =
    isOpen &&
    purchaserId != null &&
    cached == null &&
    distributionCenters.length === 0 &&
    error == null;

  const handleSaved = useCallback(() => {
    invalidateMasterDataListCacheScopes([
      "distribution-centers",
      "purchaser-dc-modal",
    ]);
    setPage(1);
    setDistributionCenters([]);
    setTotal(0);
    setError(null);
    setReloadToken((token) => token + 1);
    refetchPurchasers();
  }, [refetchPurchasers, setPage, setDistributionCenters, setTotal, setError, setReloadToken]);

  const {
    form,
    formMode,
    isSaving,
    isFormSubmittable,
    setIsFormSubmittable,
    initialValues,
    purchaserOptions,
    purchasersLoading,
    isPurchaserLocked,
    lockedPurchaserName,
    openCreate,
    openEdit,
    closeForm,
    handleSubmit,
  } = useMasterDataDistributionCenterForm({
    onSaved: handleSaved,
    lockedPurchaserId: purchaserId,
    lockedPurchaserName: purchaserName,
  });

  const centersById = useMemo(
    () => indexDistributionCentersById(items),
    [items],
  );

  const columns = useMemo(
    () => buildColumns(openEdit, centersById),
    [openEdit, centersById],
  );
  const dataSource = useMemo(
    () => mapDistributionCentersToRows(items),
    [items],
  );
  const tableWidth = useMemo(
    () => sumMasterDataColumnWidths(columns),
    [columns],
  );

  const handleClose = useCallback(() => {
    closeForm();
    setReloadToken(0);
    onClose();
  }, [closeForm, onClose, setReloadToken]);

  useEffect(() => {
    if (!isOpen || purchaserId == null || cacheKey == null) {
      return;
    }
    if (getMasterDataListCacheEntry(cacheKey)) {
      return;
    }

    let cancelled = false;
    const useCase = new GetMasterDataDistributionCentersUseCase(
      new MasterDataDistributionCenterRepository(),
    );

    void useCase
      .execute(
        new GetMasterDataDistributionCentersDTO({
          purchaserId,
          search: null,
          page_number: page,
          page_size: pageSize,
        }),
      )
      .then((result) => {
        if (cancelled) return;
        setMasterDataListCacheEntry(cacheKey, {
          items: result.items,
          total: result.total,
          page: result.page,
          limit: result.limit,
        });
        setDistributionCenters(result.items);
        setTotal(result.total);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setDistributionCenters([]);
        setTotal(0);
        setError(
          err instanceof Error
            ? err.message
            : "Failed to load distribution centers",
        );
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, purchaserId, page, pageSize, brandKey, reloadToken, cacheKey]);

  const title = purchaserName
    ? `Distribution Centers — ${purchaserName}`
    : "Distribution Centers";

  const isAddDcDisabled = loading || formMode != null;
  const showPagination = itemTotal > pageSize;
  const formStrip =
    formMode != null ? (
      <MasterDataDistributionCenterAddEditForm
        form={form}
        mode={formMode}
        initialValues={initialValues}
        isPurchaserLocked={isPurchaserLocked}
        lockedPurchaserName={lockedPurchaserName}
        purchaserOptions={purchaserOptions}
        purchasersLoading={purchasersLoading}
        isSaving={isSaving}
        isFormSubmittable={isFormSubmittable}
        onSubmit={handleSubmit}
        onCancel={() => closeForm()}
        onFormValidityChange={setIsFormSubmittable}
      />
    ) : null;

  let body = null;
  if (loading && dataSource.length === 0 && formMode == null) {
    body = (
      <Flex align="center" justifyContent="center" className="py-6">
        <Spin tip="Loading..." spinning />
      </Flex>
    );
  } else if (error) {
    body = (
      <>
        {formStrip}
        <Paragraph className="text-danger m-0">{error}</Paragraph>
      </>
    );
  } else if (dataSource.length === 0) {
    body = (
      <>
        {formStrip}
        <EmptyState
          className="bg-white border-1 border-color-grey-25 px-4 py-5 m-4"
          icon={Icons.InfoOctagon}
          title={
            <Heading level={6} className="text-grey-700 font-bold mt-3">
              No distribution centers for this purchaser.
            </Heading>
          }
        />
      </>
    );
  } else {
    body = (
      <>
        {formStrip}
        <Table<MasterDataDistributionCenterRow>
          columns={columns}
          dataSource={dataSource}
          rowHoverable
          aria-label="Purchaser distribution centers"
          sticky
          scroll={{ x: tableWidth, y: 360 }}
        />
      </>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="large"
      closable
      classNames={{
        body: "master-data-json-preview-modal__body",
      }}
      headerContent={
        <Flex
          align="center"
          justifyContent="space-between"
          gap={1}
          className="w-100"
        >
          <Heading level={6} className="text-grey-700 font-bold m-0">
            {title}
          </Heading>
          <HasAnyPermission requiredPermissions={[Permissions.MASTER_DATA_ADD]}>
            <Button
              size="small"
              variant="outline"
              onPress={openCreate}
              isDisabled={isAddDcDisabled}
            >
              <Icon slot="start" name={Icons.Plus} />
              Add DC
            </Button>
          </HasAnyPermission>
        </Flex>
      }
      footerContent={
        <Flex
          align="center"
          justifyContent="center"
          className="w-100 mb-2"
          style={{ position: "relative" }}
        >
          {showPagination ? (
            <Pagination
              totalItems={itemTotal}
              itemsPerPage={pageSize}
              currentPage={page}
              onPageChange={setPage}
              isDisabled={loading}
              aria-label="Purchaser distribution centers pagination"
            />
          ) : null}
          <Button
            size="small"
            color="grey"
            onPress={handleClose}
            style={{ position: "absolute", right: 0 }}
          >
            Close
          </Button>
        </Flex>
      }
    >
      {body}
    </Modal>
  );
};
